import { CLAIM_REJECT_CODE_OTHER } from '@constants/app';
import { Box, FormHelperText } from '@mui/material';
import {
  AutoComplete,
  ReadOnly,
  SnackbarApiErrorMessage,
  TextField,
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { useEffect, useState, forwardRef } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

const ClaimsRejectReasons = forwardRef(({ control, field, ...props }, ref) => {
  const nameKey = 'description';
  const valueKey = 'codeValue';

  const [rejectReason, setRejectReason] = useState({});
  const [reasonsList, setReasonsList] = useState([]);
  const { formatMessage } = useLocalization();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const { getValues, register, setValue } = useFormContext();
  const currentState = getValues(props?.name);

  const snackbar = useSnackbar();

  const getRejectReasonCode = async () => {
    const result = await get(`${getApiUrl()}/sxcl/v1/codetypes`);
    if (result.ok) {
      const rejectReasonResult = result?.data?.data?.find(
        item => item.codeTypeName === 'CLAIM_REJECT_CODES',
      );
      return rejectReasonResult;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={result?.errors} />,
    });

    return null;
  };

  const getRejectReasonsList = async searchValue => {
    let searchParams = '';
    if (searchValue && searchValue.length >= 2) {
      searchParams = `&filter.description=$contains:${searchValue}`;
    }
    const result = await get(
      `${getApiUrl()}/sxcl/v1/codetypes/${
        rejectReason?.id
      }/entries?limit=50&filter.isActive=$eq:true${searchParams}`,
    );
    if (result.ok) {
      const reasonsOptions =
        result?.data?.data?.map(item => ({
          label: item[nameKey],
          value: item[valueKey],
        })) || [];
      return reasonsOptions;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={result?.errors} />,
    });
    return null;
  };

  const loadOptions = async (searchValue, callback) => {
    if (!searchValue) return [];
    const resultsData = await getRejectReasonsList(searchValue);
    if (resultsData) {
      return callback(resultsData);
    }
    return callback([]);
  };

  const initialLoad = async () => {
    const result = await getRejectReasonCode();
    setRejectReason(result);
  };

  useEffect(() => {
    initialLoad();
  }, []);

  useEffect(() => {
    const fetchRejectReasons = async () => {
      if (rejectReason?.id) {
        const result = await getRejectReasonsList();
        setReasonsList(result);
      }
    };
    fetchRejectReasons();
  }, [rejectReason]);

  useEffect(() => {
    if (currentState?.value !== CLAIM_REJECT_CODE_OTHER) {
      setValue(field?.dependencies?.other?.name);
    }
  }, [currentState]);

  const { isHidden, isReadOnly } = useEntityPermission();

  if (isReadOnly(props?.permission, currentState)) {
    return <ReadOnly name={props?.name}>{currentState?.label}</ReadOnly>;
  }

  if (isHidden(props?.permission)) {
    return (
      <input
        type="hidden"
        name={props?.name}
        value={currentState?.value || ''}
      />
    );
  }

  const { errors } = useFormState({
    control,
    name: field?.dependencies?.other?.name,
  });

  const error = errors?.[field?.dependencies?.other?.name];

  return (
    <Box>
      <AutoComplete
        ref={ref}
        defaultOptions={reasonsList}
        loadOptions={loadOptions}
        {...props}
        isClearable
        inPopUp
      />
      {currentState?.value === CLAIM_REJECT_CODE_OTHER && (
        <Box sx={{ mt: 1 }}>
          <TextField
            {...register(field?.dependencies?.other?.name)}
            permission="WRITE"
            placeholder={formatMessage(field?.dependencies?.other?.placeholder)}
            sx={{ width: '100%' }}
          />
          {error?.message ? (
            <FormHelperText
              sx={{ typography: 'body2' }}
              error={Boolean(error?.message)}>
              {error?.message}
            </FormHelperText>
          ) : null}
        </Box>
      )}
    </Box>
  );
});

export default observer(ClaimsRejectReasons);
